import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  Modal,
  FormLayout,
  TextField,
  Select,
  Avatar,
  Spinner,
  Banner,
  InlineStack,
  BlockStack,
} from '@shopify/polaris'
import toast from 'react-hot-toast'

import { useAuth } from '@/contexts/AuthContext'
import { storeApi, workspaceApi } from '@/services/api'
import type { Agent } from '@/types'

type Role = 'ADMIN' | 'MANAGER' | 'AGENT' | 'SUPPORT'

const ROLE_OPTIONS: { label: string; value: Role }[] = [
  { label: 'Agent — handle conversations', value: 'AGENT' },
  { label: 'Manager — agents + analytics', value: 'MANAGER' },
  { label: 'Admin — full access', value: 'ADMIN' },
  { label: 'Support — view only', value: 'SUPPORT' },
]

const ROLE_STATUS: Record<string, 'success' | 'info' | 'attention' | undefined> = {
  ADMIN: 'success',
  MANAGER: 'info',
  AGENT: undefined,
  SUPPORT: 'attention',
}

export default function TeamPage() {
  const { activeStoreId, user, organization } = useAuth()
  const queryClient = useQueryClient()
  const storeId = activeStoreId || user?.storeId || ''

  // Owners/admins (org-level) or admins/managers (agent role) can manage the team.
  const canManage =
    (!!organization?.role && ['OWNER', 'ADMIN'].includes(organization.role)) ||
    (!!user?.role && ['ADMIN', 'MANAGER'].includes(user.role))

  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'AGENT' as Role })

  const { data, isLoading } = useQuery({
    queryKey: ['team-agents', storeId],
    queryFn: () => storeApi.getAgents(storeId),
    enabled: !!storeId && canManage,
  })

  const agents: Agent[] = data?.data.data.agents || []

  const inviteMutation = useMutation({
    mutationFn: () => workspaceApi.inviteAgent(storeId, form),
    onSuccess: () => {
      toast.success(`${form.name} added to the team`)
      setModalOpen(false)
      setForm({ name: '', email: '', password: '', role: 'AGENT' })
      queryClient.invalidateQueries({ queryKey: ['team-agents', storeId] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to add agent')
    },
  })

  const setField = (field: keyof typeof form) => (value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const canSubmit = form.name && form.email && form.password.length >= 8

  if (!canManage) {
    return (
      <Page title="Team">
        <Banner status="warning">
          You don't have permission to manage team members. Ask a workspace owner or admin.
        </Banner>
      </Page>
    )
  }

  return (
    <Page
      title="Team"
      subtitle="Salespeople who can send and manage customer conversations"
      primaryAction={{ content: 'Invite agent', onAction: () => setModalOpen(true) }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner accessibilityLabel="Loading team" size="large" />
              </div>
            ) : (
              <ResourceList
                resourceName={{ singular: 'agent', plural: 'agents' }}
                items={agents}
                emptyState={
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <Text as="p" color="subdued">
                      No team members yet. Invite your first salesperson.
                    </Text>
                  </div>
                }
                renderItem={(agent: Agent) => {
                  const activeCount = agent._count?.assignedConversations ?? 0
                  return (
                    <ResourceItem id={agent.id} onClick={() => {}}>
                      <InlineStack align="space-between" blockAlign="center">
                        <InlineStack gap="300" blockAlign="center">
                          <Avatar customer size="medium" name={agent.name} />
                          <BlockStack gap="050">
                            <Text as="span" fontWeight="semibold">
                              {agent.name}
                            </Text>
                            <Text as="span" color="subdued">
                              {agent.email}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" color="subdued">
                            {activeCount} active
                          </Text>
                          <Badge status={ROLE_STATUS[agent.role]}>{agent.role}</Badge>
                          {!agent.isActive && <Badge status="critical">Inactive</Badge>}
                        </InlineStack>
                      </InlineStack>
                    </ResourceItem>
                  )
                }}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Invite an agent"
        primaryAction={{
          content: 'Add agent',
          onAction: () => inviteMutation.mutate(),
          loading: inviteMutation.isPending,
          disabled: !canSubmit,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setModalOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Name"
              value={form.name}
              onChange={setField('name')}
              autoComplete="name"
              requiredIndicator
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={setField('email')}
              autoComplete="email"
              requiredIndicator
            />
            <TextField
              label="Temporary password"
              type="password"
              value={form.password}
              onChange={setField('password')}
              helpText="At least 8 characters. Share it with the agent so they can sign in."
              autoComplete="new-password"
              requiredIndicator
            />
            <Select
              label="Role"
              options={ROLE_OPTIONS}
              value={form.role}
              onChange={(value) => setForm(prev => ({ ...prev, role: value as Role }))}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  )
}
